import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { detectCountryCode } from '../geo/geoLocation';
import { fetchUsdRates } from './currencyRates';
import { getCurrencyForCountry, DEFAULT_CURRENCY } from './countryToCurrency';

function formatAmount(amount, currencyCode) {
  const isWhole = Math.round(amount * 100) % 100 === 0;
  try {
    return new Intl.NumberFormat(navigator?.language || 'en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: isWhole ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (e) {
    // Unknown/unsupported currency code for Intl - fall back to plain USD formatting.
    return `$${amount.toFixed(isWhole ? 0 : 2)}`;
  }
}

const CurrencyContext = createContext({
  currencyCode: DEFAULT_CURRENCY,
  countryCode: null,
  rate: 1,
  loading: true,
  convert: (usdAmount) => usdAmount,
  format: (usdAmount) => formatAmount(usdAmount, DEFAULT_CURRENCY),
});

export function CurrencyProvider({ children }) {
  const [state, setState] = useState({
    currencyCode: DEFAULT_CURRENCY,
    countryCode: null,
    rate: 1,
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const countryCode = await detectCountryCode();
      const targetCurrency = getCurrencyForCountry(countryCode);
      const rates = await fetchUsdRates();
      const targetRate = targetCurrency !== DEFAULT_CURRENCY
        ? rates?.[targetCurrency.toLowerCase()]
        : 1;

      if (cancelled) return;

      const resolvedHasRate = typeof targetRate === 'number' && targetRate > 0;
      const resolved = {
        countryCode,
        currencyCode: resolvedHasRate ? targetCurrency : DEFAULT_CURRENCY,
        rate: resolvedHasRate ? targetRate : 1,
        loading: false,
      };
      console.info('[currency] resolved:', resolved, resolvedHasRate ? '' : '(no rate available for detected currency, defaulted to USD)');
      setState(resolved);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => {
    const convert = (usdAmount) => {
      if (typeof usdAmount !== 'number' || Number.isNaN(usdAmount)) return usdAmount;
      return usdAmount * state.rate;
    };
    const format = (usdAmount) => {
      const converted = convert(usdAmount);
      if (typeof converted !== 'number') return usdAmount;
      return formatAmount(converted, state.currencyCode);
    };
    return { ...state, convert, format };
  }, [state]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}

export default CurrencyContext;
